// @flow
// FlyerGrid Container
/* global window */

import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { Link } from '../../../../shared/components/Link'
import * as R from 'ramda'
import moment from 'moment'
import qs from 'qs'
import idx from 'idx'

import * as Metric from 'models/metrics'
import * as Impression from 'models/impressions'
import sortFlyers from './sortFlyers'
import { GradeLevels, toOrdinal } from '../../../../shared/utils/grades'

import {
  fetchDistrictFlyersAndSchoolsForSchool,
  fetchDistrictFlyersAndSchools,
  setCategoryFilter
} from './redux'
import FlyerGrid from './FlyerGrid'
import { redirectToNewParentApp } from '../../../../shared/utils/redirectToNewParentApp'

type OwnProps = {
  match: { params: { resource: string, resourceId: string } }
}

type ReduxStateProps = {|
  flyers: Array<Object>, // !FIXME create flyer type. Shame on Justin.
  viewOptions: Array<Object>, // !FIXME create viewOption type. Shame on Justin.
  categoryOptions: Array<Object>, // !FIXME create categoryOption type. Shame on Justin.
  schools: Array<Object>, // !FIXME create school type. Shame on Justin.
  school: Object, // !FIXME create school type. Shame on Justin.
  district: Object, // !FIXME create district type. Shame on Justin.
  districtId: string,
  flyersLoading: boolean,
  districtsLoading: boolean,
  currentCategory: string,
  currentFlyerSort: string,
  coBrandingUrl: string,
  coBrandingAlt: string,
  coBrandingLogoLink: string,
  sheetsSaved: number,
  treesSaved: number,
  disclaimer: string,
  resourceName: string,
  resource: string,
  resourceId: string,
  districtError: string,
  schoolError: string,
  lastUpdated: number,
  showCounters: boolean
|}

type ReduxDispatchProps = {|
  // !FIXME create action types instead of any. Shame on Justin.
  fetchDistrictFlyersAndSchools: (districtId: string) => any,
  fetchDistrictFlyersAndSchoolsForSchool: (schoolId: string) => any,
  setCategoryFilter: (category: string) => any
|}

type Props = ReduxStateProps & ReduxDispatchProps & OwnProps

function normalize(str) {
  return str.trim().toLowerCase()
}

function getCategory(category) {
  const norm = normalize(category)
  if (norm === 'school') {
    return 'School'
  }
  if (norm === 'community') {
    return 'Community'
  }
  return 'all'
}

class FlyerGridContainer extends PureComponent<Props> {
  componentDidMount() {
    const { resource, resourceId } = this.props
    if (window.location.search.length > 0) {
      window.location.search
        .slice(1)
        .split('&')
        .forEach(pair => {
          const param = pair.split('=')
          if (
            param.length === 2 &&
            normalize(param[0]) === 'category' &&
            ['all', 'school', 'community'].includes(normalize(param[1]))
          ) {
            this.props.setCategoryFilter(getCategory(param[1]))
          }
        })
    }

    if (resource === 'schools') {
      this.props.fetchDistrictFlyersAndSchoolsForSchool(resourceId)
    } else if (resource === 'districts') {
      this.props.fetchDistrictFlyersAndSchools(resourceId)
    }
  }

  componentDidUpdate(prevProps) {
    if (this.isUpdateImpressionable(prevProps, this.props)) {
      // If this props change indicates a "final" render of the landing page,
      // initiate impressions AJAX call.
      this.handleImpressions()
    }

    if (this.props.districtId !== prevProps.districtId) {
      const { resource, resourceId, districtId } = this.props

      redirectToNewParentApp({
        resource,
        resourceId,
        districtId
      })
    }
  }

  // Returns boolean indicating whether this props update is indicative of a
  // flyerboard "impression" (ie, final render of a grid of flyers).  There is
  // some trickiness to this because the detail flyer and the grid view use the
  // same flyers array, so the Grid view has a glitchy inital "single flyer
  // render" in some cases before performing its "final render".  Refactoring
  // to correct this is outside of the scope of this change, though.
  isUpdateImpressionable(prevProps, props) {
    return (
      // Invariants
      // Not a sort change
      props.currentSortOption.value === prevProps.currentSortOption.value &&
      // Not a category change
      props.currentCategory === prevProps.currentCategory &&
      // Not loading
      !props.flyersLoading && // In the district case, no additional cases required
      // Additional cases by resource type
      (props.resource === 'districts' ||
        // In the school case, also count:
        (props.resource === 'schools' &&
          // When the resource type is swiched (ie, switched to school view)
          (prevProps.resource !== props.resource ||
            // When the resourceId is switched (ie, switched to a diff school)
            prevProps.resourceId !== props.resourceId ||
            // When the flyers just exited loading state
            prevProps.flyersLoading)))
    )
  }

  handleImpressions() {
    const { resource, resourceId } = this.props

    const data = {
      flyerIds: this.props.flyers.map(f => f.flyer_id)
    }

    if (resource === 'districts') {
      data.districtId = resourceId
      data.schoolId = null
    } else if (resource === 'schools') {
      data.schoolId = resourceId
      data.districtId = null
    }
    const impressionsMetrics = {
      flyerIds: data.flyerIds,
      district:
        // eslint-disable-next-line no-nested-ternary
        data.districtId
          ? typeof data.districtId === 'string'
            ? data.districtId
            : data.districtId.toString()
          : null,
      school:
        // eslint-disable-next-line no-nested-ternary
        data.schoolId
          ? typeof data.schoolId === 'string'
            ? data.schoolId
            : data.schoolId.toString()
          : null,
      eventTime: moment().format(),
      action: data.districtId ? 'flyerboard-district' : 'flyerboard-school'
    }

    // @richard - I'm assuming our new V2 metrics is collecting these impressions.
    // Impression.postOne({ body: data })
    Impression.postMetrics({ body: impressionsMetrics })
    return false
  }

  // Abstract this to a Metrics Duck
  handleMetricClick = ({ flyerId, resource }) => () => {
    const lowerResource = resource.replace(/\s/g, '').toLowerCase()
    const resourceProp = this.props.resource
    const resourceId = this.props.resourceId

    let context = { flyerId }
    if (resourceProp === 'districts') {
      context = { ...context, districtId: resourceId, schoolId: undefined }
    } else if (resourceProp === 'schools') {
      context = { ...context, schoolId: resourceId, districtId: undefined }
    }

    const data = {
      resource: lowerResource,
      activity: 'clicked',
      source: 'web',
      eventDate: Date.now(),
      userAgent: window.navigator.userAgent,
      context
    }

    const v2MetricsData = {
      flyerIds: [flyerId],
      district: context.districtId || null,
      school: context.schoolId || null,
      action: 'thumbnail-view',
      eventTime: moment().format(),
      userAgent: window.navigator.userAgent
    }
    Metric.postOne({ body: data })
    Metric.postMetrics({ body: v2MetricsData })
    return false
  }

  handleCategoryChange = (
    e: SyntheticMouseEvent<>,
    data: { value: string }
  ) => {
    e.preventDefault()
    this.props.setCategoryFilter(data.value)
  }

  render() {
    // handle OCR requests
    // eslint-disable-next-line no-undef
    const referrer = document.referrer
    const ocrEnabled = idx(this.props.district, _ => _.ocr_page_enabled) || 0
    const ocrReferrerDomain =
      idx(this.props.district, _ => _.ocr_referrer_domain) || null

    if (
      ocrEnabled === 1 &&
      ocrReferrerDomain &&
      referrer.indexOf(ocrReferrerDomain) > -1
    ) {
      window.location.replace('https://cdn.peachjar.com/peachjar.html')
      return null
    }

    return (
      <FlyerGrid
        {...this.props}
        handleCategoryChange={this.handleCategoryChange}
        handleMetricClick={this.handleMetricClick}
      />
    )
  }
}

const styles = {
  fontBlackOpaque: { color: 'black', opacity: 1 },
  fontBold: { fontWeight: 'bold' }
}

// FIXME! - Easy (blocked) - Flow type district and then add to this fn
const getDistrictOption = (district, queryString) => {
  const districtFlyerCount = R.pathOr(0, ['flyers', 'length'], district)
  return [
    {
      id: `districts/${district.district_id}/${queryString}`,
      text: district.name,
      value: `districts/${district.district_id}/${queryString}`,
      key: `districts/${district.district_id}/${queryString}`,
      description: (
        <span
          aria-label="Number of flyers"
          style={
            districtFlyerCount === 0 ? styles.fontBold : styles.fontBlackOpaque
          }
        >
          {districtFlyerCount}
        </span>
      ),
      as: Link,
      to: `/flyers/all/districts/${district.district_id}/${queryString}`
    }
  ]
}
// FIXME! - Easy (blocked) - Flow type school and then add to this fn
const getSchoolOptions = (schools, queryString) =>
  R.map(school => {
    const schoolFlyerCount = R.pathOr(0, ['flyers', 'length'], school)

    return {
      id: `schools/${school.school_id}/${queryString}`,
      text: school.name,
      value: `schools/${school.school_id}/${queryString}`,
      key: `schools/${school.school_id}/${queryString}`,
      description: (
        <span
          aria-label="Number of flyers"
          style={
            schoolFlyerCount === 0 ? styles.fontBold : styles.fontBlackOpaque
          }
        >
          {schoolFlyerCount}
        </span>
      ),
      as: Link,
      to: `/flyers/all/schools/${school.school_id}/${queryString}`
    }
  }, schools)

// FIXME! - Easy (blocked) - Flow type school and district and then add to this fn
const getViewOptions = (district, schools, queryString) => {
  const districtOption = district
    ? getDistrictOption(district, queryString)
    : []
  const schoolOptions = Array.isArray(schools)
    ? getSchoolOptions(schools, queryString)
    : []

  const sortedSchoolOptions = R.sortBy(
    R.compose(R.toLower, R.prop('text')),
    schoolOptions
  )

  return [...districtOption, ...sortedSchoolOptions]
}

// FIXME! - Easy (blocked) - Flow type categories and then add to this fn
const getCategoryOptions = categories => {
  const catOptions = R.map(id => {
    const catFlyerCount = R.pathOr(0, ['flyers', 'length'], categories[id])

    return {
      key:
        categories[id].name === 'All'
          ? // Prepending 0 to ensure All is first when sorting by key
            `0_${categories[id].name}`
          : categories[id].name,
      text: categories[id].name,
      value: categories[id].name,
      description: (
        <span
          aria-label="Number of flyers"
          style={catFlyerCount === 0 ? styles.fontBold : styles.fontBlackOpaque}
        >
          {catFlyerCount}
        </span>
      )
    }
  }, categories.allIds)

  const sortedCatOpts = R.sortBy(
    R.compose(R.toLower, R.prop('key')),
    catOptions
  )
  return [...sortedCatOpts]
}

// FIXME! - Easy (blocked) - Flow type school and district and then add to this fn
const getResourceBasedProps = (resource, resourceId, district, schools) => {
  // Default branding to district
  let coBrandingUrl = district.branding.logo_url
  let coBrandingAlt = district.branding.logo_alt
  let coBrandingLogoLink = district.branding.logo_link_url

  // Set sane type-accurate defaults
  let resourceName = ''
  let sheetsSaved = 0
  let treesSaved = 0
  const disclaimer = district.accessibility_disclaimer || ''
  let flyerIds = []
  let showCounters = true

  if (resource === 'districts' && typeof district === 'object') {
    resourceName = district.name
    treesSaved = district.tree_counter
    sheetsSaved = district.sheet_counter
    flyerIds = district.flyers

    // !FIXME - Easy - Verify API is working and remove line
    if (district.district_id === '5030') {
      showCounters = false
    }
  } else if (
    resource === 'schools' &&
    typeof schools === 'object' &&
    typeof schools[resourceId] === 'object'
  ) {
    const school = schools[resourceId]
    resourceName = school.name
    coBrandingUrl = school.branding.logo_url || district.branding.logo_url
    coBrandingAlt = school.branding.logo_alt || district.branding.logo_alt
    coBrandingLogoLink =
      school.branding.logo_link_url || district.branding.logo_link_url
    sheetsSaved = school.sheet_counter
    treesSaved = school.tree_counter
    flyerIds = school.flyers
    showCounters = school.display_tree_count

    if (school.district_id === '5030' || school.district === '5030') {
      showCounters = false
    }
  }

  return {
    coBrandingUrl,
    coBrandingAlt,
    coBrandingLogoLink,
    resourceName,
    sheetsSaved,
    treesSaved,
    disclaimer,
    flyerIds,
    showCounters
  }
}

/* eslint-disable no-underscore-dangle */
// $FlowFixMe
const isSchoolSeller = R.pipe(R.prop('category'), R.equals(R.__, 'School'))
/* eslint-enable no-underscore-dangle */

const byRecentlyPosted = R.descend(R.prop('start_timestamp'))
const bySchoolsFirst = R.descend(isSchoolSeller)

const homogenizeString = R.pipe(
  R.trim,
  R.toLower,
  R.replace(/^(a|an|the)[ \t]+/, '')
)
const getHomogenizedTitle = R.pipe(R.prop('title'), homogenizeString)

const sortByRecent = gradeLevels => flyers =>
  sortFlyers(
    flyers,
    gradeLevels.length > 0 ? gradeLevels : Object.values(GradeLevels)
  )
const sortByTitle = flyers => R.sortBy(getHomogenizedTitle, flyers)
const sortBySchoolsAndRecent = flyers =>
  R.sortWith([bySchoolsFirst, byRecentlyPosted], flyers)

const sortByUpcoming = flyers => {
  const now = moment().unix()
  const thePast = moment('1999-09-09T00:00:00Z').unix()

  const flyersWithUnixEventDates = flyers.map(flyer => {
    const parsedDate = moment(flyer.event_date)
    const unixDate = parsedDate.isValid() ? parsedDate.unix() : thePast

    return { flyer, unixDate }
  })

  let sortedFlyers = R.sortBy(
    ({ unixDate }) => unixDate - now,
    flyersWithUnixEventDates
  )

  // Filter out past events here, too
  sortedFlyers = R.filter(({ unixDate }) => unixDate - now > 0, sortedFlyers)

  return R.map(R.prop('flyer'), sortedFlyers)
}

const getQueryParamsFromQueryString = queryString =>
  qs.parse(queryString, { ignoreQueryPrefix: true })

const getSortOption = (value, options) =>
  R.find(R.propEq('value', value))(options)
const getSortOptions = (location, gradeLevels) => {
  const queryParams = getQueryParamsFromQueryString(location.search)
  const queryStringRecent = qs.stringify({ ...queryParams, sort: 'recent' })
  const queryStringUpcoming = qs.stringify({ ...queryParams, sort: 'upcoming' })
  const queryStringSchool = qs.stringify({ ...queryParams, sort: 'school' })
  const queryStringTitle = qs.stringify({ ...queryParams, sort: 'title' })

  return [
    {
      value: 'recent',
      text: 'Recently Posted',
      sort: sortByRecent(gradeLevels),
      as: Link,
      to: `${location.pathname}?${queryStringRecent}`
    },
    {
      value: 'upcoming',
      text: 'Upcoming Events',
      sort: sortByUpcoming,
      as: Link,
      to: `${location.pathname}?${queryStringUpcoming}`
    },
    {
      value: 'school',
      text: 'School Flyers (first)',
      sort: sortBySchoolsAndRecent,
      as: Link,
      to: `${location.pathname}?${queryStringSchool}`
    },
    {
      value: 'title',
      text: 'Title (A-Z)',
      sort: sortByTitle,
      as: Link,
      to: `${location.pathname}?${queryStringTitle}`
    }
  ]
}

// !FIXME - Medium - Figure out why props: OwnProps is breaking R.pathOr and fix
// !FIXME - Medium - type state to match combined reducers (via export)
const mapStateToProps = (state, props) => {
  const defaultSortValue = 'recent'
  const resource = R.pathOr(null, ['match', 'params', 'resource'], props)
  const resourceId = R.pathOr(null, ['match', 'params', 'resourceId'], props)
  const queryString = R.pathOr('', ['location', 'search'], props)
  const queryParams = getQueryParamsFromQueryString(queryString)
  const querySort = queryParams.sort
  const district = state.districts.district
  const schools = state.schools
  const categoryFilter = state.flyerGrid.categoryFilter
  const districtSchools = R.map(id => state.schools[id], district.schools)
  const nonStaffSchools = R.filter(R.propEq('staff', false), districtSchools)
  const flyerAudienceInfo = R.pathOr(
    null,
    ['flyers', 'flyerAudienceInfo'],
    state
  )
  const respectSchoolEndFilter =
    resource === 'schools' && flyerAudienceInfo != null

  const {
    coBrandingUrl,
    coBrandingAlt,
    coBrandingLogoLink,
    resourceName,
    sheetsSaved,
    treesSaved,
    disclaimer,
    flyerIds,
    showCounters
  } = getResourceBasedProps(resource, resourceId, district, schools)

  // By default, parent users (no staff param) hide everything staff related
  let groupsToHide = ['staff', 'district-staff', 'school-staff']
  let overriddenResourceName = resourceName
  const qsParams = getQueryParamsFromQueryString(window.location.search)
  const staffParam = qsParams['staff']
  const schoolsParam = qsParams['schools']
  const schoolsWhitelist = schoolsParam ? schoolsParam.split(',').map(sId => normalize(sId)) : []

  // Staff users hide school flyers and staff flyers for different staff groups
  if (staffParam === 'district') {
    groupsToHide = ['parent-group', 'school', 'school-staff']
    overriddenResourceName = resourceName ? `Staff of ${resourceName}` : resourceName
  } else if (staffParam === 'school') {
    groupsToHide = ['parent-group', 'school', 'district-staff']
    overriddenResourceName = resourceName ? `Staff of ${resourceName}` : resourceName
  }

  // Hide any flyers who are only sent to schools with hidden transitionalGroupType.
  function gradeRangeIncludesChildren(min, max) {
    const $min = min || GradeLevels.EarlyChildhoodEducation
    const $max = max || GradeLevels.ContinuingEducation
    return props.profile.children.every(c => {
      return c.gradeLevel === GradeLevels.NotGraded ||
      (toOrdinal(c.gradeLevel) <= toOrdinal($max) && toOrdinal(c.gradeLevel) >= toOrdinal($min))
    })
  }
  let flyersArray = flyerIds
    .map(fId => state.flyers[fId])
    .filter(f => (
      (f.category === 'Community' || gradeRangeIncludesChildren(f.min_target_grade, f.max_target_grade))
      // Returns true if a school which is not hidden type exists for this flyer or it's on the whitelist
      && (f.schools.find(s => schoolsWhitelist.includes(s) || !groupsToHide.includes(state.schools[s].transitionalGroupType)))
    ))


  // !FIXME: In the future, this will be state.categories
  const categoriesById = R.reduce(
    (accum, flyer) => {
      const all = accum.All
      const catName = flyer.category
      const cat = accum[catName] || {
        name: catName,
        flyers: []
      }

      // If we don't care about the school actual end date filter OR...
      if (
        !respectSchoolEndFilter ||
        // ...we care about the school actual end date filter AND...
        (respectSchoolEndFilter &&
          // ...this flyer is marked active for the audience (defaulting to
          // true if unknown)...
          R.pathOr(
            true,
            [flyer.flyer_id.toString(), resourceId],
            flyerAudienceInfo
          ))
      ) {
        // ...then add the flyer to the list of visibles
        cat.flyers = [...cat.flyers, flyer.flyer_id]
        all.flyers = R.uniq([...all.flyers, flyer.flyer_id])
      }

      return { ...accum, All: all, [catName]: cat }
    },
    {
      All: {
        name: 'All',
        flyers: []
      }
    },
    flyersArray
  )

  const filteredFlyers = R.map(
    fId => state.flyers[fId],
    categoriesById[categoryFilter] ? categoriesById[categoryFilter].flyers : []
  )
  const sortOptions = getSortOptions(props.location, props.profile.children.map(c => c.gradeLevel))
  const sortOption =
    getSortOption(querySort, sortOptions) ||
    getSortOption(defaultSortValue, sortOptions) ||
    sortOptions[0]

  // !FIXME: In the future, this will be state.categories.allIds
  const categoryIds = Object.keys(categoriesById)
  // !FIXME: In the future, this will be state.categories
  const categories = { ...categoriesById, allIds: categoryIds }

  return {
    flyers: sortOption.sort(filteredFlyers),
    district,
    districtId: district.district_id,
    flyersLoading: state.flyers.isFetching,
    districtsLoading: state.schools.isFetching,
    viewOptions: getViewOptions(district, nonStaffSchools, queryString),
    categoryOptions: getCategoryOptions(categories),
    sortOptions,
    currentCategory: state.flyerGrid.categoryFilter,
    currentSortOption: sortOption,
    schools: nonStaffSchools,
    school: state.schools[resourceId],
    coBrandingUrl,
    coBrandingAlt,
    coBrandingLogoLink,
    sheetsSaved,
    treesSaved,
    disclaimer,
    resourceName: overriddenResourceName,
    resource,
    resourceId,
    districtError: state.districts.error,
    schoolError: state.schools.error,
    lastUpdated: state.flyers.lastUpdated,
    showCounters
  }
}

const mapDispatchToProps = dispatch => ({
  fetchDistrictFlyersAndSchools: districtId =>
    dispatch(fetchDistrictFlyersAndSchools(districtId)),
  fetchDistrictFlyersAndSchoolsForSchool: schoolId =>
    dispatch(fetchDistrictFlyersAndSchoolsForSchool(schoolId)),
  setCategoryFilter: category => dispatch(setCategoryFilter(category))
})

export default connect(mapStateToProps, mapDispatchToProps)(FlyerGridContainer)



// WEBPACK FOOTER //
// ./src/screens/Flyers/screens/FlyerGrid/index.js